import { FC, useCallback, useState } from 'react'
import { DateTime } from 'luxon'
import { StickiedPageHeader } from 'components/page-header/StickiedPageHeader'
import {
  AllocationTables,
  TimeCards,
  Header,
  WorkDayTable,
  TimeLoggerTaskPicker as TaskPicker,
} from '../components'
import { LeaveSidebar } from 'features/leave-sidebar'
import { useInhibitKeyPressOverride } from '@hooks/useInhibitKeyPressOverride'
import { useWeekDates } from '@hooks/useWeekDates'
import { useWeekStartsOn } from '@hooks/useWeekStartsOn'
import { Banner } from 'components/notifications'
import { useTranslation } from 'react-i18next'
import { useEmployee } from '@hooks/useEmployee'
import { useUserId } from '../hooks/useUserId'
import { BannerMessageContext } from 'types'
import { useBannerMessagesForContext } from '@hooks/useBannerMessages'
import { BannerMessage } from 'components/banner-message'

export const TimeLogging: FC = () => {
  const userId = useUserId()
  const { t } = useTranslation()
  const [hoverDate, setHoverDate] = useState<DateTime | null>(null)
  const [taskSidebarHidden, setTaskSidebarHidden] = useState(true)
  const [leaveSidebarHidden, setLeaveSidebarHidden] = useState(true)

  // This sidebar requests data that may never otherwise be necessary
  // if it is never opened. So instead of loading that data
  // every time the Time Logging page is loaded, we render the sidebar
  // the first time it's opened, which triggers the initial data load,
  // and then we *hide* it from there to retain its state.
  const [loadTaskSidebar, setLoadTaskSidebar] = useState(false)

  const weekStartsOn = useWeekStartsOn()
  const { range, start } = useWeekDates({ weekStartsOn })
  const { registerMenuClosed, registerMenuOpened } =
    useInhibitKeyPressOverride()

  const showTaskSidebar = () => {
    setLoadTaskSidebar(true)
    setTaskSidebarHidden(false)
    registerMenuOpened('task-sidebar')
  }

  const showLeaveSidebar = () => {
    setLeaveSidebarHidden(false)
    registerMenuOpened('leave-sidebar')
  }

  const hideTaskSidebar = useCallback(() => {
    setTaskSidebarHidden(true)
    registerMenuClosed('task-sidebar')
  }, [setTaskSidebarHidden, registerMenuClosed])

  const hideLeaveSidebar = useCallback(() => {
    setLeaveSidebarHidden(true)
    registerMenuClosed('leave-sidebar')
  }, [setLeaveSidebarHidden, registerMenuClosed])

  const employeeQuery = useEmployee({ userId, startDate: start })

  const bannerMessages = useBannerMessagesForContext(
    BannerMessageContext.TimeLogging,
  )

  // TODO - bring in read only information from somewhere, likely from each time card
  // const readOnly = selectedPeriod.status != EmployeePayPeriodStatus.Open
  const readOnly = false

  return (
    <div id="main" className="relative flex flex-row no-wrap grow">
      <div className="grow">
        {loadTaskSidebar && (
          <TaskPicker hidden={taskSidebarHidden} onClose={hideTaskSidebar} />
        )}
        {/* We don't care if the leave sidebar resets when hidden, so we only need to render it when visible */}
        {!leaveSidebarHidden && (
          <LeaveSidebar hidden={false} onClose={hideLeaveSidebar} />
        )}
        <StickiedPageHeader>
          <Header
            hoverDate={hoverDate}
            onTaskButtonClick={showTaskSidebar}
            onLeaveButtonClick={showLeaveSidebar}
          />
          {employeeQuery.data?.adminManagedGroup && (
            <Banner
              content={t('features.timeLogging.timeCardsAreManagedByAdmins')}
              dismissable={false}
              title={t('common.adminManaged')}
              variant="info"
            />
          )}
          {bannerMessages.map((message) => (
            <BannerMessage key={message.id} message={message} />
          ))}
          <TimeCards
            onTimeOffClick={() => setLeaveSidebarHidden(false)}
            readOnly={readOnly}
          />
        </StickiedPageHeader>
        <WorkDayTable onCellHover={setHoverDate} readOnly={readOnly} />
        <AllocationTables
          dateRange={range}
          headerHoverHandler={setHoverDate}
          onTaskButtonClick={showTaskSidebar}
          readOnly={readOnly}
        />
      </div>
    </div>
  )
}
